import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import './checkInDialog.css';

export interface RemoveDeviceDialogProps {
  open: boolean;
  /** "Device 2 · Laptop — Won't boot", for naming what is about to go. */
  label: string;
  /** Charges already composed against this device, if any. */
  chargesTotal?: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Destructive confirmation for removing a device from a check-in
 * (sm4rt-web-ui skill §6 — a destructive confirmation gets the user's full
 * attention). On-system shell from the shared `checkInDialog.css`, matching
 * `CancelSessionDialog`: portal + backdrop, hairline header with close ×,
 * recessive "Keep device" and a solid-red confirm.
 *
 * Only offered while the device has no RepairShopr ticket — see
 * `canDeleteDevice`, which mirrors the server's rule. So this dialog never has
 * to explain an orphaned ticket, because that case cannot reach it.
 */
export const RemoveDeviceDialog: React.FC<RemoveDeviceDialogProps> = ({
  open,
  label,
  chargesTotal,
  onClose,
  onConfirm,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  if (!open) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove the device');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) handleClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label="Remove device">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Remove device</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          <div className="ci-dialog__desc">
            <strong>{label}</strong> will be removed from this check-in, along with its
            inspection and any photos. No RepairShopr ticket exists for it yet, so nothing
            is left behind there.
            {!!chargesTotal && chargesTotal > 0 && (
              <>
                {' '}
                Its preliminary charges come off the check-in total.
              </>
            )}
          </div>

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button
            type="button"
            className="ci-dialog__cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Keep device
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--danger"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Removing…' : 'Remove device'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
